import React, {useEffect, useRef} from 'react';

const FrontloadingPhase = ({
  data,
  activeIndex = 0,
  finished = false,
  startDisabled = false,
  onStart,
  onRestart,
  showDisclaimer = false,
  onDismissDisclaimer,
  listeningEnabled = true,
  showRestart = true,
}) => {
  if (!data) return null;

  const {terms} = data;
  const activeCardRef = useRef(null);

  useEffect(() => {
    if (!listeningEnabled) return;
    activeCardRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }, [activeIndex, listeningEnabled]);

  return (
    <div className="lcv-frontload-panel">
      {showDisclaimer && (
        <div className="lcv-fl-disclaimer">
          <div className="lcv-fl-disclaimer-icon">i</div>
          <div className="lcv-fl-disclaimer-body">
            <strong>Prototype Preview</strong>
            <span>
              The 3D avatar and metaverse background are for layout reference
              only. The final experience will be first-person. Please focus on
              evaluating the frontloading <em>UI/UX</em> elements.
            </span>
          </div>
          <button
            type="button"
            className="lcv-fl-disclaimer-close"
            onClick={onDismissDisclaimer}
          >
            ✕
          </button>
        </div>
      )}

      <div className="lcv-frontload-header">
        <span className="lcv-fl-badge">INTRO</span>
        <h2 className="lcv-fl-title">Key Terms for This Lesson</h2>
        <p className="lcv-fl-subtitle">
          {terms.length} vocabulary terms to master
        </p>
      </div>

      <div className="lcv-fl-list">
        {terms.map((term, index) => {
          const isActive = listeningEnabled && index === activeIndex && !finished;
          const isDone = listeningEnabled
            ? index < activeIndex ||
              (finished && index === terms.length - 1) ||
              (finished && index < terms.length)
            : false;

          return (
            <div
              key={term.term || index}
              ref={index === activeIndex ? activeCardRef : undefined}
              className={[
                'lcv-fl-card',
                isActive ? 'lcv-fl-active' : '',
                isDone && !isActive ? 'lcv-fl-done' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className="lcv-fl-num">
                {isDone && !isActive ? '✓' : index + 1}
              </span>

              <div className="lcv-fl-content">
                <div className="lcv-fl-term">{term.term}</div>
                <div className="lcv-fl-def">{term.definition}</div>
              </div>

              {listeningEnabled && isActive && (
                <div className="lcv-fl-active-indicator">
                  <span className="lcv-fl-active-pulse" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {finished ? (
        <div className="lcv-fl-buttons">
          {listeningEnabled && showRestart && (
            <button
              type="button"
              className="lcv-btn-secondary lcv-btn-watch-again"
              onClick={onRestart}
            >
              ↻ Watch Again
            </button>
          )}
          <button
            type="button"
            className="lcv-btn-primary lcv-btn-full lcv-fl-start-btn"
            onClick={onStart}
            disabled={startDisabled}
          >
            Start Learning →
          </button>
          {startDisabled && (
            <div className="lcv-fl-progress-hint">
              <span className="lcv-fl-listening-dot" />
              Preparing lesson audio...
            </div>
          )}
        </div>
      ) : (
        <div className="lcv-fl-progress-hint">
          <span className="lcv-fl-listening-dot" />
          Listening... {Math.min(activeIndex + 1, terms.length)} of {terms.length}
        </div>
      )}
    </div>
  );
};

export default FrontloadingPhase;
